import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import type { Role } from '@tekvoice/shared';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import { formatDateTime } from '@/lib/utils';

/** SPEC §9 — invite / disable / enable / re-invite. No mailer in v1: the URL is shown once. */
export function UsersSettingsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users.list() });

  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState<Role>('EMPLOYEE');
  const [lastInviteUrl, setLastInviteUrl] = React.useState<string | null>(null);

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const invite = useMutation({
    mutationFn: () => api.users.invite({ email, role }),
    onSuccess: async (result) => {
      setLastInviteUrl(result.inviteUrl);
      setEmail('');
      toast.success(`Invite created for ${result.user.email}`);
      await invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const reinvite = useMutation({
    mutationFn: (id: string) => api.users.reinvite(id),
    onSuccess: async (result) => {
      setLastInviteUrl(result.inviteUrl);
      toast.success('New invite link created');
      await invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const disable = useMutation({
    mutationFn: (id: string) => api.users.disable(id),
    onSuccess: async (result) => {
      toast.success(`Disabled ${result.user.email} (${result.sessionsRevoked} session(s) revoked)`);
      await invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const enable = useMutation({
    mutationFn: (id: string) => api.users.enable(id),
    onSuccess: async (result) => {
      toast.success(`Enabled ${result.user.email}`);
      await invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Invite a teammate</CardTitle>
          <CardDescription>Single-use link, valid for 7 days. No self-signup.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              invite.mutate();
            }}
          >
            <div className="min-w-64 flex-1 space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={invite.isPending}
              />
            </div>
            <div className="w-48 space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select value={role} onValueChange={(value) => setRole(value as Role)}>
                <SelectTrigger id="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EMPLOYEE">Employee</SelectItem>
                  <SelectItem value="SUPER_ADMIN">Super admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={invite.isPending}>
              <UserPlus className="h-4 w-4" />
              {invite.isPending ? 'Inviting…' : 'Invite'}
            </Button>
          </form>

          {lastInviteUrl ? (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p className="mb-1 font-medium">Send this link to the invitee — it is shown once:</p>
              <code className="break-all text-xs">{lastInviteUrl}</code>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>Disabling a user immediately deletes their sessions.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last seen</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(users.data?.users ?? []).map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {user.role === 'SUPER_ADMIN' ? 'Super admin' : 'Employee'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.disabledAt ? (
                      <Badge variant="destructive">Disabled</Badge>
                    ) : user.pendingInvite ? (
                      <Badge variant="warning">Invite pending</Badge>
                    ) : (
                      <Badge variant="success">Active</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTime(user.lastSeenAt)}
                  </TableCell>
                  <TableCell className="space-x-2 text-right">
                    {user.pendingInvite ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={reinvite.isPending}
                        onClick={() => reinvite.mutate(user.id)}
                      >
                        Re-invite
                      </Button>
                    ) : null}
                    {user.disabledAt ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={enable.isPending}
                        onClick={() => enable.mutate(user.id)}
                      >
                        Enable
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={disable.isPending}
                        onClick={() => disable.mutate(user.id)}
                      >
                        Disable
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {users.isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
